import _ from 'lodash';
import {connect} from 'react-redux';

import {
  isChannelValidated,
  mapChannelToChannelType,
} from '../../../../../../../../../../src/helpers/models/channelHelpers';
import {globalNamespace} from '../../../../../../../../../../src/helpers/models/namespaceHelpers';
import {CardModel} from '../../../../../../../../../../src/models/api/cardModel';
import {ChannelComposeModesEnum} from '../../../../../../../../../../src/models/api/channelBehaviorsModel';
import {ChannelTypesEnum} from '../../../../../../../../../../src/models/api/channelCommonTypes';
import {ChannelModel} from '../../../../../../../../../../src/models/api/channelModel';
import {
  buildContactFromContactUid,
  ContactSourcesEnum,
  parseContactUid,
} from '../../../../../../../../../../src/models/api/contactModel';
import {RecipientRolesEnum} from '../../../../../../../../../../src/models/api/recipientModel';
import {AppState} from '../../../../../../../../../../src/store/appState';
import {ThunkAction} from '../../../../../../../../../../src/store/appThunk';
import {makeGetCards} from '../../../../../../../../../../src/store/data/cards/cardsSelectors';
import {getUserParticipatingChannels} from '../../../../../../../../../../src/store/data/channels/channelsSelectors';
import {composeMessageAction} from '../../../../../../../../../../src/store/data/messages/draftMessagesActions';
import {compareName} from '../../../../../../../../../../srcCommon/helpers/localeHelpers';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
} from '../../../../../../../../../../srcCommon/helpers/reselectHelpers';
import {ChannelTypesToIdMap, ContactsTableMessageAction} from './contactsTableMessageAction';

/*
 * Props.
 */

interface ContactsTableMessageActionContainerProps {
  cardIds: ReadonlyArray<number>;
}

/*
 * Selectors.
 */

function getCardIds(_state: AppState, props: ContactsTableMessageActionContainerProps) {
  return props.cardIds;
}

function makeGetChannelTypes(getCards: ReturnType<typeof makeGetCards>) {
  return createSelector(
    [getCards, getUserParticipatingChannels],
    (
      cards: ReadonlyArray<CardModel>,
      channels: ReadonlyArray<ChannelModel>,
    ): ReadonlyArray<ChannelTypesEnum> =>
      _(cards)
        .map((card) => card.contactUids)
        .flatten()
        .map((contactUid) => parseContactUid(contactUid)[0])
        .map((source) =>
          channels.filter((channel) => isChannelValid(channel, source)).map(mapChannelToChannelType),
        )
        .flatten()
        .compact()
        .sort((a, b) => compareName(a, b))
        .uniq()
        .value(),
  );
}

function makeGetChannelTypesToIdMap() {
  return createSelector([getUserParticipatingChannels], (channels: ReadonlyArray<ChannelModel>) =>
    _(channels)
      .filter((channel) => isChannelValid(channel))
      .reduce((result, channel) => {
        const channelType = mapChannelToChannelType(channel);
        if (result[channelType]) {
          return {
            ...result,
            [channelType]: [...result[channelType], channel.id],
          };
        }
        return {
          ...result,
          [channelType]: [channel.id],
        };
      }, {} as ChannelTypesToIdMap),
  );
}

/*
 * Helpers.
 */

function isChannelValid(channel: ChannelModel, source?: ContactSourcesEnum) {
  // We will not support bulk messaging to custom channels.
  if (channel.type === ChannelTypesEnum.CUSTOM) {
    return false;
  }
  // Make sure the channel source matches the source we are looking for.
  if (source && channel.contactSource !== source) {
    return false;
  }
  // We can only send from channels that support sending.
  if (channel.behaviors.composeMode !== ChannelComposeModesEnum.NORMAL) {
    return false;
  }
  return isChannelValidated(channel);
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<ContactsTableMessageActionContainerProps>()(() => {
  const getCards = makeGetCards(getCardIds);
  return {
    selectedCards: getCards,
    channelTypes: makeGetChannelTypes(getCards),
    channelTypesToId: makeGetChannelTypesToIdMap(),
  };
});

const mapDispatchToProps = makeMapDispatchToProps()((props) => ({
  onComposeMessage:
    (contactUids: ReadonlyArray<string>, channelId?: number): ThunkAction =>
    (dispatch) => {
      const recipients = contactUids.map((contactUid) => ({
        contact: buildContactFromContactUid(contactUid),
        namespace: globalNamespace,
        role: RecipientRolesEnum.TO,
      }));
      dispatch(composeMessageAction({recipients, shouldForcePopout: true, channelId}));
    },
}));

export const ContactsTableMessageActionContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(ContactsTableMessageAction);
